/**
 * React Query hooks for contact directory sources (ADR-141 §8).
 *
 * Source writes invalidate the whole scope (list + per-source sync
 * logs). Sync-now does NOT invalidate the contacts/tags scopes — the
 * sync runs async server-side (202), so contact rows land later; the
 * sources scope is invalidated so status/last-sync columns refetch.
 * Sync-log queries are keyed per source.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createDirectorySource,
  deleteDirectorySource,
  getDirectorySources,
  getDirectorySourceSyncLog,
  patchDirectorySource,
  syncDirectorySourceNow,
  type ContactDirectorySource,
  type ContactDirectorySourcePatchPayload,
  type ContactDirectorySourceWritePayload,
} from '../../services/contacts.service';

export const directorySourceKeys = {
  all: ['contactDirectorySources'] as const,
  list: () => [...directorySourceKeys.all, 'list'] as const,
  syncLog: (sourceId: number) =>
    [...directorySourceKeys.all, 'syncLog', sourceId] as const,
};

export function useDirectorySources(enabled = true) {
  return useQuery({
    queryKey: directorySourceKeys.list(),
    queryFn: getDirectorySources,
    enabled,
  });
}

function useInvalidateSources() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: directorySourceKeys.all });
  };
}

export function useCreateDirectorySource() {
  const invalidate = useInvalidateSources();
  return useMutation<
    ContactDirectorySource,
    Error,
    ContactDirectorySourceWritePayload
  >({
    // Wrapped so React Query's context second-arg never leaks into the
    // service signature.
    mutationFn: (body) => createDirectorySource(body),
    onSuccess: invalidate,
  });
}

export function usePatchDirectorySource() {
  const invalidate = useInvalidateSources();
  return useMutation<
    ContactDirectorySource,
    Error,
    { sourceId: number; body: ContactDirectorySourcePatchPayload }
  >({
    mutationFn: ({ sourceId, body }) => patchDirectorySource(sourceId, body),
    onSuccess: invalidate,
  });
}

export function useDeleteDirectorySource() {
  const invalidate = useInvalidateSources();
  return useMutation<void, Error, number>({
    mutationFn: (sourceId) => deleteDirectorySource(sourceId),
    onSuccess: invalidate,
  });
}

export function useSyncDirectorySourceNow() {
  const invalidate = useInvalidateSources();
  return useMutation<void, Error, number>({
    mutationFn: (sourceId) => syncDirectorySourceNow(sourceId),
    onSuccess: invalidate,
  });
}

/** Last 50 sync-log rows for one source; disabled until a source is picked. */
export function useDirectorySourceSyncLog(sourceId: number | null) {
  return useQuery({
    queryKey: directorySourceKeys.syncLog(sourceId ?? -1),
    queryFn: () => getDirectorySourceSyncLog(sourceId as number),
    enabled: sourceId != null,
  });
}
